import { useCart } from "@/lib/CartContext";
import { Minus, Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import {
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
  SheetFooter,
} from "@/components/ui/sheet";

export default function CartSheet({ onCheckout }) {
  const { cart, removeItem, updateQuantity, subtotal } = useCart();

  const sizeLabels = { small: "S", medium: "M", large: "L" };

  return (
    <SheetContent className="flex flex-col bg-[#FFFBEB] w-full sm:max-w-md">
      <SheetHeader className="pb-4">
        <SheetTitle className="font-heading text-2xl text-[#78350F]">
          Your Order
        </SheetTitle>
        <SheetDescription className="font-body text-stone-500">
          {cart.length === 0
            ? "Your cart is empty"
            : `${cart.length} item${cart.length > 1 ? "s" : ""} in your cart`}
        </SheetDescription>
      </SheetHeader>

      <ScrollArea className="flex-1 -mx-6 px-6" data-testid="cart-items-scroll">
        {cart.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <div className="w-16 h-16 rounded-full bg-stone-100 flex items-center justify-center mb-4">
              <span className="text-2xl">~</span>
            </div>
            <p className="font-body text-stone-500 text-sm">
              Browse our menu and add some delicious items!
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {cart.map((item) => (
              <div
                key={item.cartId}
                data-testid={`cart-item-${item.cartId}`}
                className="bg-white rounded-xl p-4 border border-stone-100 animate-scale-in"
              >
                <div className="flex items-start justify-between mb-2">
                  <div className="flex-1 min-w-0">
                    <h4 className="font-body font-semibold text-sm text-[#78350F] truncate">
                      {item.name}
                    </h4>
                    <div className="flex items-center gap-2 mt-1">
                      <span
                        className={`inline-block w-2.5 h-2.5 rounded-sm border ${
                          item.isVeg
                            ? "bg-green-500 border-green-600"
                            : "bg-red-500 border-red-600"
                        }`}
                      />
                      <span className="text-xs text-stone-400 font-body">
                        {item.pricingType === "per_piece" || item.size === "per_piece"
                          ? "Per Piece"
                          : `${item.size === "med_large" ? "Med-Large" : item.size.charAt(0).toUpperCase() + item.size.slice(1)} Tray`}
                      </span>
                    </div>
                  </div>
                  <button
                    data-testid={`remove-item-${item.cartId}`}
                    onClick={() => removeItem(item.cartId)}
                    className="p-1.5 rounded-lg hover:bg-red-50 text-stone-400 hover:text-red-500 transition-colors"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <button
                      data-testid={`qty-minus-${item.cartId}`}
                      onClick={() =>
                        updateQuantity(item.cartId, item.quantity - 1)
                      }
                      className="h-7 w-7 rounded-full border border-stone-200 flex items-center justify-center hover:bg-stone-50 transition-colors"
                    >
                      <Minus className="h-3 w-3" />
                    </button>
                    <span
                      data-testid={`qty-value-${item.cartId}`}
                      className="font-body font-semibold text-sm w-6 text-center"
                    >
                      {item.quantity}
                    </span>
                    <button
                      data-testid={`qty-plus-${item.cartId}`}
                      onClick={() =>
                        updateQuantity(item.cartId, item.quantity + 1)
                      }
                      className="h-7 w-7 rounded-full border border-stone-200 flex items-center justify-center hover:bg-stone-50 transition-colors"
                    >
                      <Plus className="h-3 w-3" />
                    </button>
                  </div>
                  <span className="font-body font-bold text-[#EA580C]">
                    ${(item.price * item.quantity).toFixed(2)}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </ScrollArea>

      {cart.length > 0 && (
        <div className="pt-4">
          <Separator className="mb-4" />
          <div className="flex justify-between items-center mb-4">
            <span className="font-body text-stone-500 text-sm">Subtotal</span>
            <span
              data-testid="cart-subtotal"
              className="font-heading text-2xl font-bold text-[#78350F]"
            >
              ${subtotal.toFixed(2)}
            </span>
          </div>
          <SheetFooter className="sm:flex-col gap-2">
            <Button
              data-testid="checkout-button"
              onClick={onCheckout}
              className="w-full rounded-full bg-[#EA580C] hover:bg-[#C2410C] text-white font-body font-semibold h-12 text-base shadow-lg hover:shadow-xl transition-all active:scale-95"
            >
              Proceed to Checkout
            </Button>
          </SheetFooter>
        </div>
      )}
    </SheetContent>
  );
}
